import React, {useEffect, useState} from 'react';
import Lottie from 'lottie-react';
import {useNavigate} from 'react-router-dom';
import {
  GAME_INTRO_CONVERSATIONS,
  POST_ANSWER_CONVERSATIONS,
  ConversationBlock,
  CARD_COLORS,
  CardColor,
  ChatQuestion,
  ChatQuestionOption,
} from './config';
import ConversationSequence, {ActionButton} from './components/ConversationSequence';
import useChatConversations from './hooks/useChatConversations';
import useChatQuestions from './hooks/useChatQuestions';
import CardGame from './components/CardGame';
import QuestionModal from './components/QuestionModal';
import UserQuestionModal from './components/UserQuestionModal';
import EndModal from './components/EndModal';
import yetiHappyDanceJson from '../../appAssets/jsons/YetiHappyDance.json';
import backButtonJson from '../../appAssets/jsons/back_button.json';
import chatBG from '../../appAssets/chat-bg.png';
import tableIpad from '../../appAssets/table-ipad.png';
import tablePhone from '../../appAssets/table-phone.png';
import WebShadowButton from '../WebStoryScreen/Components/WebShadowButton';
import {trackEvent} from '../../../mixpanel';
import {TrackingEvents} from '../../Utilities/analytics';

type Phase = 'set1' | 'set2' | 'game' | 'set3';

const INTRO_BLOCK_NAMES = ['Chat Intro Block 1', 'Chat Intro Block 2'];
const CLOSING_BLOCK_NAME = 'Chat Closing Block';

const ChatWithTrooScreenWeb = () => {
  const navigate = useNavigate();
  const isLarge = window.innerWidth >= 768;

  const {showChatConvo, blocks, loading} = useChatConversations();
  const {
    questions,
    loading: questionsLoading,
    refetch: refetchQuestions,
  } = useChatQuestions(false);
  const {questions: trooQuestions, refetch: refetchTrooQuestions} =
    useChatQuestions(true);

  const [phase, setPhase] = useState<Phase | null>(null);
  const [actionButton, setActionButton] = useState<ActionButton | null>(null);
  const [activeQuestion, setActiveQuestion] = useState<ChatQuestion | null>(null);
  const [activeCardColor, setActiveCardColor] = useState<CardColor | null>(null);
  const [showTrooQuestionModal, setShowTrooQuestionModal] = useState(false);
  const [isTrooAnswerRound, setIsTrooAnswerRound] = useState(false);
  const [showUserQuestionModal, setShowUserQuestionModal] = useState(false);
  const [trooAnswerBlock, setTrooAnswerBlock] = useState<ConversationBlock | null>(null);
  const [showEndModal, setShowEndModal] = useState(false);

  useEffect(() => {
    if (!loading) {
      setPhase(showChatConvo ? 'set1' : 'set2');
    }
  }, [loading, showChatConvo]);

  const introBlocks = INTRO_BLOCK_NAMES.map(name =>
    blocks.find(block => block.name === name),
  ).filter((block): block is ConversationBlock => !!block);

  const conversationData: ConversationBlock[] =
    phase === 'set1'
      ? introBlocks
      : phase === 'set3'
      ? POST_ANSWER_CONVERSATIONS
      : GAME_INTRO_CONVERSATIONS;

  const handlePhaseComplete = () => {
    if (phase === 'set1') {
      setActionButton(null);
      setPhase('set2');
    } else if (phase === 'set2') {
      setActionButton(null);
      setPhase('game');
    } else if (phase === 'set3') {
      setActionButton(null);
      setIsTrooAnswerRound(true);
      setPhase('game');
    }
  };

  const handleCardPicked = (card: CardColor) => {
    const idx = CARD_COLORS.indexOf(card);
    trackEvent(TrackingEvents.object_clicked, {
      object_name: card,
      from: 'chat_with_troo',
    });
    setActiveCardColor(card);
    if (isTrooAnswerRound) {
      setActiveQuestion(trooQuestions[idx] ?? null);
      setShowUserQuestionModal(true);
    } else {
      setActiveQuestion(questions[idx] ?? null);
      setShowTrooQuestionModal(true);
    }
  };

  const closeQuestion = () => {
    setShowTrooQuestionModal(false);
    setShowUserQuestionModal(false);
    setActiveQuestion(null);
  };

  const handleAnswerSubmit = (option: ChatQuestionOption) => {
    setShowTrooQuestionModal(false);
    setActiveQuestion(null);
    setPhase('set3');
  };

  const handleAskTroo = (answer: ChatQuestionOption) => {
    setShowUserQuestionModal(false);
   trackEvent(TrackingEvents.child_answers_to_troo, {
      type: activeQuestion?.type,
      complexity: activeQuestion?.complexity,
      selCompetency: activeQuestion?.selCompetency,
      answer: answer.option,
    });
    setActiveQuestion(null);
    setTrooAnswerBlock({
      conversations: [
        {
          object: [
            {
              content: answer.option,
              audio: answer.optionAudioUrl ?? '',
              duration: answer.optionAudioDuration ?? '0',
            },
          ],
        },
      ],
      userActions: [],
    });
  };

  const handleTrooAnswerComplete = () => {
    setTrooAnswerBlock(null);
    setShowEndModal(true);
  };

  const endModalData = (() => {
    const closingBlock = blocks.find(block => block.name === CLOSING_BLOCK_NAME);
    const objects = closingBlock?.conversations?.[0]?.object ?? [];
    if (!objects.length) return null;
    const obj = objects[Math.floor(Math.random() * objects.length)];
    return {
      title: obj.content,
      animationUrl: obj.blockObjects?.[0]?.url ?? obj.characterImage ?? null,
      audioUrl: obj.audio,
    };
  })();

  const handleHome = () => {
    trackEvent(TrackingEvents.object_close_icon_clicked, {
      from: 'chat_with_troo',
    });
    navigate(-1);
  };

  const handleRestart = () => {
    trackEvent(TrackingEvents.chat_game_replayed);
    setShowEndModal(false);
    setActiveCardColor(null);
    setActiveQuestion(null);
    setIsTrooAnswerRound(false);
    setTrooAnswerBlock(null);
    setPhase('set2');
    refetchQuestions();
    refetchTrooQuestions();
  };

  const yetiSize = isLarge ? 300 : 230;
  const tableHeightPx = Math.round(window.innerHeight * 0.38);

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundImage: `url(${chatBG})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: isLarge ? 28 : 16,
    paddingRight: isLarge ? 28 : 16,
    paddingTop: isLarge ? 18 : 12,
    paddingBottom: 4,
  };

  const yetiContainerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    position: 'relative',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button
          style={{background: 'none', border: 'none', cursor: 'pointer', padding: 4}}
          onClick={handleHome}>
          <Lottie
            animationData={backButtonJson}
            style={{width: isLarge ? 52 : 40, height: isLarge ? 52 : 40}}
            loop={false}
            autoplay={false}
          />
        </button>
        <span style={{
          fontFamily: 'Mochiy Pop One',
          fontSize: isLarge ? 24 : 20,
          color: '#333',
          marginLeft: isLarge ? -52 : -36,
        }}>
          The Troo-th Table
        </span>
        <div style={{width: isLarge ? 52 : 40}} />
      </div>

      {/* Main area: Yeti + conversation bubbles */}
      <div style={yetiContainerStyle}>
        {phase !== null && phase !== 'game' && (
          <ConversationSequence
            key={phase}
            data={conversationData}
            onActionButton={setActionButton}
            onComplete={handlePhaseComplete}
          />
        )}
        {phase === 'game' && trooAnswerBlock && (
          <ConversationSequence
            key="troo-answer"
            data={[trooAnswerBlock]}
            onActionButton={() => {}}
            onComplete={handleTrooAnswerComplete}
          />
        )}
        <Lottie
          animationData={yetiHappyDanceJson}
          style={{width: yetiSize, height: yetiSize, marginBottom: tableHeightPx - yetiSize * 0.3}}
          loop
        />
      </div>

      {/* Table */}
      <img
        src={isLarge ? tableIpad : tablePhone}
        alt=""
        style={{
          width: '100%',
          height: tableHeightPx,
          position: 'absolute',
          bottom: 0,
          objectFit: 'fill',
        }}
      />

      {/* Action button (Continue / Next) */}
      {actionButton && (
        <div style={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 10,
        }}>
          <WebShadowButton
            title={actionButton.text}
            variant="text"
            labelStyle={{ fontFamily: 'Mochiy Pop One', fontSize: isLarge ? 18 : 14 }}
            onPress={actionButton.onPress}
          />
        </div>
      )}

      {/* Cards */}
      {phase === 'game' && (
        <CardGame
          onCardPicked={handleCardPicked}
          initialSelectedCard={activeCardColor ?? 'orange'}
          hidePickButton={!!trooAnswerBlock}
          tableHeight={tableHeightPx}
        />
      )}

      {/* Modals */}
      {activeCardColor && showTrooQuestionModal && (
        <QuestionModal
          question={activeQuestion}
          cardColor={activeCardColor}
          onSubmit={handleAnswerSubmit}
          onClose={closeQuestion}
        />
      )}
      {showEndModal && endModalData && (
        <EndModal
          title={endModalData.title}
          animationUrl={endModalData.animationUrl}
          onHome={handleHome}
          onRestart={handleRestart}
          audioUrl={endModalData.audioUrl}
        />
      )}
      {activeCardColor && showUserQuestionModal && (
        <UserQuestionModal
          questions={trooQuestions}
          cardColor={activeCardColor}
          onCardColorChange={color => {
            const idx = CARD_COLORS.indexOf(color);
            setActiveCardColor(color);
            setActiveQuestion(trooQuestions[idx] ?? null);
          }}
          onAskTroo={handleAskTroo}
          onClose={closeQuestion}
        />
      )}

      {/* Loading overlay */}
      {(loading || questionsLoading) && (
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(255,255,255,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            width: 40,
            height: 40,
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #9747FF',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
};

export default ChatWithTrooScreenWeb;
